import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import clsx from 'clsx'
import Icon from '../../../../atoms/Icon'
import { createSiblingCellContext } from './utils'
import CollapsibleColumnHeader from './CollapsibleColumnHeader'

export const DEFAULT_CLASSES = {
  header: '',
  cell: '',
  footer: ''
}

export const DEFAULT_STYLES = {
  header: undefined,
  cell: undefined,
  footer: undefined
}

function mapHeaderClasses (defaults, header, other = '', includeHeader = true) {
  const col = header?.column?.columnDef ?? {}
  const sortable = header.column.getCanSort()
  const isCollapsible = col?.isCollapsible
  return clsx(defaults, col.headerClasses, other, {
    clickable: sortable || isCollapsible,
    'select-none': sortable || isCollapsible,
    slim: !includeHeader,
    'column-group-header': col?.isGroup
  })
}

function mapHeaderLabelClasses (defaults, header) {
  const col = header?.column?.columnDef ?? {}
  return clsx('flex flex-row items-center', defaults, {
    'justify-center': col.align === 'center',
    'justify-start': !col.align || col.align === 'left',
    'justify-end': col.align === 'right'
  })
}

function mapCellClasses (defaults, cell, other = '') {
  const col = cell?.column?.columnDef ?? {}
  return clsx(defaults, col.cellClasses, other, {
    'text-left': col.align === 'left',
    'text-right': col.align === 'right',
    'text-center': col.align === 'center'
  })
}

function mapFooterClasses (defaults, footer, other = '') {
  const col = footer?.column?.columnDef ?? {}
  return clsx(defaults, col.footerClasses, other, {
    'text-left': col.align === 'left',
    'text-right': col.align === 'right',
    'text-center': col.align === 'center'
  })
}

function mapSortIcon (header) {
  const sortDir = header.column.getIsSorted()
  if (sortDir === 'asc') return 'up'
  if (sortDir === 'desc') return 'down'
  return ''
}

function mapCellStyles (defaultStyles, highlightStyle, cell) {
  const highlight = cell?.column?.columnDef?.meta?.highlight?.(cell.row) ?? false
  const highlightStyled = highlight ? highlightStyle : {}
  return {
    ...(defaultStyles || {}),
    ...highlightStyled,
    width: cell?.column?.getSize?.()
  }
}

function mapHeaderStyles (defaultStyles, header) {
  return {
    ...(defaultStyles || {}),
    width: header.column.getSize()
  }
}

export function defaultGetSubRows (row) {
  return row.data
}

export function defaultGetSiblingRows (row) {
  if (row.siblings && Array.isArray(row.siblings)) {
    return row.siblings
  }
  if (row.benchmarks && Array.isArray(row.benchmarks)) {
    return row.benchmarks
  }
  return []
}

export function usePresentationTableExpandState (defaultExpandState) {
  const [expandState, setExpandState] = useState(defaultExpandState)

  useEffect(() => {
    setExpandState(defaultExpandState)
  }, [defaultExpandState])

  return {
    expandState,
    setExpandState
  }
}

function OpalPresentationTable ({
  data,
  columns,
  defaultSort,
  includeFooter,
  includeHeader,
  className,
  style,
  componentClasses,
  componentStyles,
  getSubRows,
  getSiblingRows,
  expandState,
  onExpandStateChange,
  isLoading = false,
  emptyStateText = 'No Data Available',
  collapsedGroups = new Set(),
  onToggleColumnGroup = () => {}
}) {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    // allows client-side sorting
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows,
    initialState: {
      sorting: defaultSort
    },
    state: {
      expanded: expandState
    },
    onExpandedChange: onExpandStateChange,
    enableSorting: !!includeHeader
  })
  const classes = useMemo(() => {
    return {
      ...DEFAULT_CLASSES,
      ...(componentClasses || {})
    }
  }, [componentClasses])
  const styles = useMemo(() => {
    return {
      ...DEFAULT_STYLES,
      ...(componentStyles || {})
    }
  }, [componentStyles])

  return (
    <table className={clsx('opal-presentation-table', className, isLoading && 'opt-loading')} style={style}>
      <thead className='opt-thead'>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className='opt-tr'>
            {headerGroup.headers.map((header) => {
              const columnDef = header.column.columnDef
              const { isGroup, groupId, isCollapsible } = columnDef
              const isCollapsed = isGroup && collapsedGroups.has(groupId)

              return (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  className={mapHeaderClasses('opt-th', header, classes.header, includeHeader)}
                  style={mapHeaderStyles(styles.header, header)}
                  onClick={!isCollapsible ? header.column.getToggleSortingHandler() : undefined}
                >
                  {includeHeader ? (
                    isCollapsible ? (
                      <CollapsibleColumnHeader
                        header={flexRender(header.column.columnDef.header, header.getContext())}
                        isCollapsed={isCollapsed}
                        onToggle={() => onToggleColumnGroup(groupId)}
                        className={mapHeaderLabelClasses('opt-th-label', header)}
                        headerClassName={mapCellClasses('', header)}
                      />
                    ) : (
                      <div className={mapHeaderLabelClasses('opt-th-label', header)}>
                        <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                        {header.column.getIsSorted() ? (
                          <span>
                            <Icon name={mapSortIcon(header)} />
                          </span>
                        ) : null}
                      </div>
                    )
                  ) : null}
                </th>
              )
            })}
          </tr>
        ))}
      </thead>
      <tbody className={clsx('opt-tbody', { 'no-footer': !includeFooter })}>
        {table.getRowModel().rows.map(row => (
          <React.Fragment key={row.id}>
            <tr className={`opt-tr opt-tr-depth-${row.depth} opt-expandable`}>
              {row.getVisibleCells().map((cell, index) => (
                <td
                  key={cell.id}
                  className={mapCellClasses('opt-td', cell, classes.cell)}
                  style={mapCellStyles(styles.cell, styles.highlight, cell)}
                >
                  {(index === 0) ? (row.getCanExpand()) ? (
                    <div className='opt-expander clickable flex items-center' onClick={(e) => document.startViewTransition(() => row.getToggleExpandedHandler()(e))}>
                      <Icon additionalClasses={clsx('expand-icon', { expanded: row.getIsExpanded() })} name='chevronRight' customSize={16} />
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  ) : (
                    <div className='opt-expander opt-lead'>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  ) : (
                    flexRender(cell.column.columnDef.cell, cell.getContext())
                  )}
                </td>
              ))}
            </tr>
            {getSiblingRows(row.original)?.length ? getSiblingRows(row.original)?.map((sibling, sIndex) => (
              <tr key={`${row.id}-sibling-${sIndex}`} className={`opt-tr opt-tr-depth-${row.depth} opt-expandable opt-sibling-tr`}>
                {row.getVisibleCells().map((cell, index) => {
                  const columnDef = cell.column.columnDef
                  const siblingCellContext = createSiblingCellContext(sibling, cell, row, sIndex)
                  return (
                    <td key={`${cell.id}-sibling-${sIndex}`} className={mapCellClasses('opt-td opt-sibling-td', cell, classes.cell)} style={mapCellStyles(styles.cell, styles.highlight, cell)}>                      {(index === 0) ? (
                      <div className={clsx('opt-expander', { 'opt-lead': !row.getCanExpand(), 'pl-5': row.getCanExpand() })}>
                        {flexRender(columnDef.cell, siblingCellContext)}
                      </div>
                    ) : flexRender(columnDef.cell, siblingCellContext)}
                    </td>
                  )
                })}
              </tr>
            )) : null}
          </React.Fragment>
        ))}
        {(!data?.length && !isLoading && !!emptyStateText?.length) ? (
          <tr className='opt-tr opt-empty-state'>
            <td className='opt-td text-center' colSpan={table.getAllColumns().length}>
              {emptyStateText}
            </td>
          </tr>
        ) : null}
      </tbody>
      {includeFooter ? (
        <tfoot className='opt-tfoot'>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id} className='opt-tr'>
              {footerGroup.headers.map(header => (
                <th key={header.id} className={mapFooterClasses('opt-th', header, classes.footer)} style={styles.footer}>
                  {flexRender(header.column.columnDef.footer, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      ) : null}
    </table>
  )
}

OpalPresentationTable.propTypes = {
  /** The data array */
  data: PropTypes.arrayOf(PropTypes.object),

  /** The table column configuration */
  columns: PropTypes.arrayOf(PropTypes.object),

  /** Determine how to find data sub-rows */
  getSubRows: PropTypes.func,

  /** Determine how to find sibling rows */
  getSiblingRows: PropTypes.func,

  /** default sort configuration */
  defaultSort: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.string
  })),

  /** show or hide the header */
  includeHeader: PropTypes.bool,

  /** show or hide the footer */
  includeFooter: PropTypes.bool,

  expandState: PropTypes.object,
  onExpandStateChange: PropTypes.func,

  className: PropTypes.string,
  style: PropTypes.object,

  /** Specifies classes to be applied to subcomponents of the table */
  componentClasses: PropTypes.shape({
    header: PropTypes.string,
    cell: PropTypes.string,
    footer: PropTypes.string
  }),
  componentStyles: PropTypes.shape({
    header: PropTypes.object,
    cell: PropTypes.object,
    highlight: PropTypes.object,
    footer: PropTypes.object
  }),
  /** Loading state */
  isLoading: PropTypes.bool,
  /** Empty state text, set to an empty string to hide */
  emptyStateText: PropTypes.string,
  /** Set of collapsed column group IDs */
  collapsedGroups: PropTypes.instanceOf(Set),
  /** Callback when a column group is toggled */
  onToggleColumnGroup: PropTypes.func
}

OpalPresentationTable.defaultProps = {
  includeHeader: true,
  getSubRows: defaultGetSubRows,
  getSiblingRows: defaultGetSiblingRows
}

export default OpalPresentationTable
